import React, { useRef } from 'react';
import { Button } from '~/shared/components/Button/Button';
import { useTranslation } from '~/shared/utils/translation';
import { useInputFieldController } from '../../../form/hooks/useInputFieldController';
import { usePaymentDetails } from '~/features/commerce-api/hooks/usePaymentDetails';
import { useBasket } from '~/features/basket/hooks';
import { useLocalBasketState } from '~/features/basket/hooks/useLocalBasketState';
import { StyledInvalidMessage } from '~/shared/components/form/components/invalid-message';
import { StyledHelpText } from '~/shared/components/form/components/help-text';
import {
    GiftCertificateWrapper,
    GiftCertificateElementsGroup,
    GiftCertificateInput,
    GiftCertificateCVC,
} from './styled';

export const GiftCertificateFormElements = ({
    isFormstateValid,
    submitLabel,
    giftCardError,
}: {
    isFormstateValid: boolean;
    submitLabel: string;
    giftCardError?: string;
}) => {
    const { translate } = useTranslation();
    const cardRef = useRef<HTMLInputElement>(null);
    const cvcRef = useRef<HTMLInputElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const {
        fieldId,
        onBlur: onCardBlur,
        isValid,
        onChange,
        value,
        showHelpText,
        error,
        isTouched,
    } = useInputFieldController('card', {
        required: true,
    });

    const {
        fieldId: fieldId2,
        onBlur: onCVCBlur,
        isValid: isValid2,
        isTouched: isTouched2,
        error: error2,
        onChange: onChange2,
        value: value2,
        showHelpText: showHelpText2,
    } = useInputFieldController('cvc', { required: false });

    const defaultValue = '';
    const helpText = '';

    const handleBlur = (_e: React.FocusEvent<HTMLInputElement>) => {
        onCardBlur();
        onCVCBlur();
    };

    const handleCardNoChange: React.ChangeEventHandler<HTMLInputElement> = (
        prop: React.ChangeEvent<HTMLInputElement>,
    ) => {
        if (prop.target.value.length == 19) {
            cvcRef.current?.focus();
        }

        return onChange(prop);
    };

    const handleCVCChange: React.ChangeEventHandler<HTMLInputElement> = (
        prop: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const result = onChange2(prop);
        return result;
    };

    const basket = useBasket();
    const { membership } = useLocalBasketState();
    const { remainingTotal } = usePaymentDetails(basket, membership);

    return (
        <GiftCertificateElementsGroup>
            <GiftCertificateWrapper>
                <GiftCertificateInput
                    disabled={remainingTotal === 0}
                    ref={cardRef}
                    id={fieldId}
                    type="text"
                    withLabel={false}
                    isValid={isValid}
                    onChange={handleCardNoChange}
                    onBlur={handleBlur}
                    value={value}
                    placeholder={translate('checkout.giftCertificateForm.placeholder')}
                    defaultValue={defaultValue}
                    maxLength={19}
                />
                <GiftCertificateCVC
                    disabled={remainingTotal === 0}
                    ref={cvcRef}
                    id={fieldId2}
                    type="text"
                    onChange={handleCVCChange}
                    onBlur={handleBlur}
                    withLabel={false}
                    isValid={isValid2}
                    value={value2}
                    placeholder={translate('checkout.giftCertificateForm.cvcPlaceholder')}
                    defaultValue=""
                    maxLength={3}
                />
                <Button type="submit" disabled={!isFormstateValid} ref={buttonRef}>
                    {submitLabel}
                </Button>
            </GiftCertificateWrapper>
            {((isTouched && error) || (isTouched2 && error2)) && (
                <StyledInvalidMessage>{(error ?? error2)?.message}</StyledInvalidMessage>
            )}
            {!!giftCardError && <StyledInvalidMessage>{giftCardError}</StyledInvalidMessage>}
            {(showHelpText || showHelpText2) && <StyledHelpText>{helpText}</StyledHelpText>}
        </GiftCertificateElementsGroup>
    );
};
